import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import { getUserServiceBrowserUrl } from "../lib/api/userServiceBaseUrl";
import { DarkModeToggle } from "../components/DarkModeToggle";
import { useI18n } from "../lib/i18n";
import * as s from "../styles/dev-captures.css";

type CapturedEmail = {
  to: string;
  subject: string;
  text?: string;
  html?: string;
  meta?: Record<string, unknown>;
  sentAt: string;
};

export const Route = createFileRoute("/captured-emails")({
  component: CapturedEmailsPage,
});

function CapturedEmailsPage() {
  const { t } = useI18n();
  const [emails, setEmails] = useState<CapturedEmail[]>([]);
  const [selected, setSelected] = useState<CapturedEmail | null>(null);
  const [loading, setLoading] = useState(true);

  const baseUrl = getUserServiceBrowserUrl();

  const fetchEmails = useCallback(async () => {
    try {
      const res = await fetch(`${baseUrl}/dev/captured-emails`);
      if (!res.ok) return;
      const data = await res.json();
      setEmails(data.emails ?? []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    fetchEmails();
    const interval = setInterval(fetchEmails, 5000);
    return () => clearInterval(interval);
  }, [fetchEmails]);

  const handleClear = async () => {
    await fetch(`${baseUrl}/dev/captured-emails`, { method: "DELETE" });
    setEmails([]);
    setSelected(null);
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className={s.page}>
      <div className={s.topBar}>
        <span className={s.topBarTitle}>{t("pro.devCaptures.title")}</span>
        <nav className={s.tabNav}>
          <Link to="/captured-emails" className={`${s.tabLink} ${s.tabLinkActive}`}>
            {t("pro.devCaptures.emails")}
          </Link>
          <Link to="/captured-sms" className={s.tabLink}>
            {t("pro.devCaptures.sms")}
          </Link>
        </nav>
        <div className={s.topBarActions}>
          <DarkModeToggle placement="header" />
        </div>
      </div>

      <div className={s.body}>
        <div className={s.sidebar}>
          <div className={s.sidebarHeader}>
            <span className={s.sidebarCount}>
              {t("pro.devCaptures.emailCount", { count: emails.length })}
            </span>
            <div className={s.sidebarActions}>
              <button type="button" className={s.refreshBtn} onClick={fetchEmails}>
                {t("pro.devCaptures.refresh")}
              </button>
              {emails.length > 0 && (
                <button type="button" className={s.clearBtn} onClick={handleClear}>
                  {t("pro.devCaptures.clearAll")}
                </button>
              )}
            </div>
          </div>

          {loading && emails.length === 0 && (
            <div className={s.emptyState}>{t("pro.devCaptures.loading")}</div>
          )}

          {!loading && emails.length === 0 && (
            <div className={s.emptyState}>
              <span>{t("pro.devCaptures.noEmails")}</span>
              <span style={{ fontSize: 12 }}>
                {t("pro.devCaptures.noEmailsHint")}
              </span>
            </div>
          )}

          {emails.map((email, idx) => (
            <div
              key={`${email.sentAt}-${idx}`}
              className={`${s.listItem} ${selected === email ? s.listItemActive : ""}`}
              onClick={() => setSelected(email)}
            >
              <div className={s.listItemTo}>{email.to}</div>
              <div className={s.listItemSubject}>{email.subject}</div>
              <div className={s.listItemTime}>{formatTime(email.sentAt)}</div>
            </div>
          ))}
        </div>

        {!selected ? (
          <div className={s.detailEmpty}>{t("pro.devCaptures.selectEmail")}</div>
        ) : (
          <div className={s.detail}>
            <div className={s.detailHeader}>
              <div className={s.detailMeta}>
                <span className={s.detailMetaLabel}>{t("pro.devCaptures.to")}</span>
                {selected.to}
              </div>
              <div className={s.detailMeta}>
                <span className={s.detailMetaLabel}>{t("pro.devCaptures.subject")}</span>
                {selected.subject}
              </div>
              <div className={s.detailMeta}>
                <span className={s.detailMetaLabel}>{t("pro.devCaptures.sent")}</span>
                {formatTime(selected.sentAt)}
              </div>
              {selected.meta && (
                <div className={s.detailMeta}>
                  <span className={s.detailMetaLabel}>{t("pro.devCaptures.meta")}</span>
                  {JSON.stringify(selected.meta)}
                </div>
              )}
            </div>

            {selected.html ? (
              <div className={s.detailBody}>
                <iframe
                  title={t("pro.devCaptures.emailPreview")}
                  srcDoc={`<base target="_blank">${selected.html}`}
                  sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                />
              </div>
            ) : selected.text ? (
              <div className={s.detailText}>{selected.text}</div>
            ) : (
              <div className={s.detailEmpty}>{t("pro.devCaptures.noContent")}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
